import { Loader } from "@progress/kendo-react-indicators";
import { useLocation, useNavigate } from "react-router-dom";
import * as React from "react";
import { axiosPost } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import { QUERY_KEYS } from "../../data/helpers/queryKeys";
import { useQueryClient } from "react-query";
import { getLocalDevAuthRequestOptions } from "../../utils/authRedirect";
import { clearSensitiveLoginUrl } from "../../utils/loginUrlCleanup";

const getQueryParamFromSearch = (search, paramName) => {
  const searchValue = search?.startsWith("?") ? search.slice(1) : search || "";
  const keyPart = searchValue
    .split("&")
    .find((item) => item.startsWith(`${paramName}=`));

  if (!keyPart) {
    return null;
  }

  const rawValue = keyPart.slice(paramName.length + 1);

  try {
    return decodeURIComponent(rawValue);
  } catch (error) {
    return rawValue;
  }
};

const Login = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isAuthenticated, setIsAuthenticated] = React.useState(1);
  const [loginErrorMessage, setLoginErrorMessage] = React.useState(
    "Failed to login. Please try navigating again from PrismHR.",
  );

  React.useEffect(() => {
    let entity = {
      token: getQueryParamFromSearch(search, "key"),
      peoId: getQueryParamFromSearch(search, "peo_id"),
    };

    if (!entity.token) {
      setLoginErrorMessage(
        "Missing PrismHR SSO token. Please try navigating again from PrismHR.",
      );
      setIsAuthenticated(3);
    } else {
      try {
        clearSensitiveLoginUrl();
      } catch (_e) {
        // non-critical: URL cleanup failed, proceed with authentication
      }
      axiosPost(
        SERVICE_URLS.USER_AUTHENTICATE,
        entity,
        getLocalDevAuthRequestOptions()
      )
        .then((resp) => {
          queryClient.invalidateQueries([QUERY_KEYS.USER_GET]);

          if (resp.data && resp.data.isAuthenticated) {
            setIsAuthenticated(2);
            if (resp.data.hasAdminAppPrivilege) {
              navigate("/admin/dashboard", { replace: true });
            } else if (resp.data.hasClientAppPrivilege) {
              navigate("/dashboard", { replace: true });
            } else {
              setLoginErrorMessage(
                "Authenticated successfully, but no Open Enrollment app privileges were assigned to this user.",
              );
              setIsAuthenticated(3);
            }
          } else {
            setLoginErrorMessage(
              resp?.data?.errorMessage ||
                "Failed to login. Please try navigating again from PrismHR.",
            );
            setIsAuthenticated(3);
          }
        })
        .catch((err) => {
          setLoginErrorMessage(
            err?.response?.data?.errorMessage ||
              err?.response?.data?.title ||
              "Failed to login. Please try navigating again from PrismHR.",
          );
          setIsAuthenticated(3);
          queryClient.invalidateQueries([QUERY_KEYS.USER_GET]);
        });
    }
  }, [search, navigate, queryClient]);

  const RenderMessage = () => {
    if (isAuthenticated === 2) {
      return (
        <span>
          Successfully logged-in. Click <a href="/admin/dashboard">here</a> to
          navigate to dashboard.
        </span>
      );
    } else {
      return <span>{loginErrorMessage}</span>;
    }
  };

  if (isAuthenticated === 1) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader size="large" type={"infinite-spinner"} />
      </div>
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#EEF2F7",
        }}
      >
        <div
          style={{
            width: "400px",
            textAlign: "center",
            height: "400px",
            backgroundColor: "#ffff",
          }}
          className={"login-form"}
        >
          <p
            style={{
              backgroundColor: "#192853",
              marginTop: "0px",
              padding: "10px",
            }}
          >
            <img
              src={"/Logo_white.svg"}
              alt={"PrestigePEO logo"}
              style={{ height: 45 }}
            />
          </p>
          <h2>SSO Login</h2>
          <p style={{ padding: "40px" }}>
            <RenderMessage />
          </p>
        </div>
      </div>
    );
  }
};

export default Login;
